import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { motion } from "framer-motion";
import ProfileHeader from "./ProfileHeader";
import ProfileBanner from "./ProfileBanner";
import RankCard from "./RankCard";
import ProfileAboutSection from "./ProfileAboutSection";
import ProfileTabBar from "./ProfileTabBar";
import ProfileOverviewTab from "./ProfileOverviewTab.jsx";
import ProfileMatchHistoryTab from "./ProfileMatchHistoryTab.jsx";
import ProfilePerformanceTab from "./ProfilePerformanceTab.jsx";
import ProfileReputationTab from "./ProfileReputationTab.jsx";
import ProfileFriendsTab from "./ProfileFriendsTab.jsx";
import ProfileLoadoutTab from "./ProfileLoadoutTab.jsx";
import { buildProfileStatsBarCharts } from "./profileStatsBarCharts";
import {
    buildProfileTabSearch,
    DEFAULT_PROFILE_TAB,
    getProfileTabFromSearch,
    getVisibleProfileTabs,
    PROFILE_TAB_IDS,
    resolveProfileTab,
} from "./profileTabConfig";
import { MAIN_SECTION_MIN_HEIGHT_SX } from "../layout/layoutConstants";
import { colors } from "../../theme/colors";
import { getEloTier } from "../common/eloTierUtils";
import utils from "../../universal/utils";
import { notifyIfCannotSendFriendRequest, notifyIfTargetCannotReceiveFriendRequest } from "../../utils/friendRequests";

const MotionBox = motion(Box);
const edgeBasePath = utils.getEdgeBasePath();
const PROFILE_LAYOUT_DESKTOP_MIN_PX = 1050;
const PROFILE_LEFT_RAIL_MAX_PX = 300;
const layoutUp = `@media (min-width: ${PROFILE_LAYOUT_DESKTOP_MIN_PX}px)`;

const MAP_NAMES = [
    "de_mirage",
    "de_inferno",
    "de_nuke",
    "de_anubis",
    "de_vertigo",
    "de_ancient",
    "de_cache",
    "de_cobblestone_2018",
    "de_dust_ii",
];

const SORT_GENERAL_VALUES = ["Date", "Win", "Loss"];

const SORT_MAPS_VALUES = [
    "All Maps",
    "Mirage",
    "Inferno",
    "Nuke",
    "Anubis",
    "Vertigo",
    "Ancient",
    "Cache",
    "Cobblestone",
    "Dust II",
];

function resolveSteamId(userLike) {
    return userLike?.steamId || userLike?.steamId64 || userLike?.steam64Id || null;
}

export default function ProfilePage({ catalog, actions }) {
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(() => getProfileTabFromSearch(location.search));
    const [friendRequestSubmittingSteamId, setFriendRequestSubmittingSteamId] = useState(null);
    const [profileFriends, setProfileFriends] = useState([]);

    const {
        user,
        profileData = {},
        profileDataLoading = false,
        matchSummary = {},
        last25MatchData = {},
        mapsStats = {},
        activityMatches = [],
        recentStats = {},
        sortOptions,
        friendRequestSuccess = null,
        friendRequestError = null,
        pendingRequests = [],
    } = catalog;

    const relationship = catalog.profileRelationship || "none";
    const profileSteamId = resolveSteamId(profileData);
    const viewerSteamId = resolveSteamId(user);
    const matchNumber = matchSummary?.totalPlayedMatches ?? "not available";
    const hasPlayedAtLeastOneMatch = Object.keys(last25MatchData || {}).length > 0;
    const isOwnProfile = profileData?._id === user?._id;
    const canViewLoadout =
        isOwnProfile ||
        String(user?._id) === String(id) ||
        String(user?.userId) === String(id);
    const profileTabs = useMemo(
        () => getVisibleProfileTabs(canViewLoadout),
        [canViewLoadout],
    );
    const viewerFriends = catalog.friends || [];
    const isViewerFriendOfProfile = useMemo(() => {
        if (!profileSteamId) return false;
        return viewerFriends.some(
            (friend) => String(friend?.friendSteamId || "") === String(profileSteamId),
        );
    }, [viewerFriends, profileSteamId]);
    const friendshipStatus = isViewerFriendOfProfile
        ? "friends"
        : relationship;
    const playerGeneralStats = profileData?.playerGeneralStats || {};
    const rating3 = profileData?.rating3;
    const statsBarCharts = useMemo(
        () => buildProfileStatsBarCharts(last25MatchData),
        [last25MatchData]
    );
    const profileStatsBarItems = [
        {
            label: "Matches",
            value: String(playerGeneralStats.totalMatchesPlayed ?? 0),
            sublabel: "All Time",
            visualType: "bar",
            visualData: statsBarCharts.matchesByDay,
        },
        {
            label: "Win Rate",
            value: `${playerGeneralStats.winRate ?? 0}%`,
            sublabel: "All Time",
            valueColor: colors.primary.main,
            visualType: "ring",
            visualData: playerGeneralStats.winRate ?? 0,
        },
        {
            label: "Avg ADR",
            value: String(playerGeneralStats.avgAdr ?? 0),
            sublabel: "All Time",
            visualType: "sparkline",
            visualData: statsBarCharts.adrValues,
        },
        {
            label: "K/D Ratio",
            value: String(playerGeneralStats.kdRatio ?? 0),
            sublabel: "All Time",
            visualType: "sparkline",
            visualData: statsBarCharts.kdValues,
        },
        {
            label: "Rating 3.0",
            value:
                rating3?.current != null
                    ? Number(rating3.current).toFixed(2)
                    : "—",
            sublabel:
                rating3?.peak != null
                    ? `Peak: ${Number(rating3.peak).toFixed(2)}`
                    : "All Time",
            visualType: "sparkline",
            visualData: statsBarCharts.rating3Values,
        },
    ];

    const currentElo = playerGeneralStats.elo ?? profileData?.elo;
    const eloTier = getEloTier(currentElo);
    const globalRankPercent = playerGeneralStats.globalRankPercent;
    const globalRank = playerGeneralStats.globalRank;
    const rankData = {
        label: eloTier.label,
        elo: Number(currentElo) || 0,
        globalRank:
            globalRankPercent != null && globalRankPercent !== ""
                ? `Top ${globalRankPercent}%`
                : "",
    };

    const startFriendsPolling = useCallback((viewerUser) => {
        actions.getFriends({
            userId: viewerUser.userId,
            steamId: resolveSteamId(viewerUser),
        });
    }, [actions]);

    const stopFriendsPolling = useCallback(() => {
        // No-op — polling removed in favour of Socket.IO push
    }, []);

    useEffect(() => {
        actions.getProfilePageUserDataByUserId(id);
    }, [id]);

    useEffect(() => {
        if (!id) {
            setProfileFriends([]);
            return undefined;
        }

        const viewingOwnProfile =
            String(user?._id) === String(id) ||
            String(user?.userId) === String(id);

        if (viewingOwnProfile) {
            setProfileFriends([]);
            return undefined;
        }

        let cancelled = false;

        fetch(`${edgeBasePath}/api/gc/public/profile/${id}/friends`)
            .then((response) => response.json())
            .then((data) => {
                if (cancelled) return;
                setProfileFriends(
                    data?.ok && Array.isArray(data.friends)
                        ? data.friends.filter((friend) => !!friend?.friendData)
                        : []
                );
            })
            .catch(() => {
                if (!cancelled) {
                    setProfileFriends([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [id, user?._id, user?.userId]);

    useEffect(() => {
        if (!user) return;
        actions.getFriends({
            userId: user.userId,
            steamId: viewerSteamId,
        });
    }, [id, user?.userId, viewerSteamId, actions, user]);

    useEffect(() => {
        if (!user) return undefined;

        actions.getLobbyByUserId(user.userId);
        startFriendsPolling(user);

        return () => {
            stopFriendsPolling();
        };
    }, [user?.userId, viewerSteamId, actions, startFriendsPolling, stopFriendsPolling, user]);

    useEffect(() => {
        if (!viewerSteamId || !profileSteamId) return;
        actions.getProfileRelationship({ viewerSteamId, targetSteamId: profileSteamId });
    }, [viewerSteamId, profileSteamId, actions]);

    const validateForm = (event) => {
        event.preventDefault();
    };

    const handleSortChange = (sortType) => (sortOption) => {
        actions.setSortOption(sortType, sortOption);
    };

    useEffect(() => {
        const nextTab = getProfileTabFromSearch(location.search);
        setActiveTab(
            nextTab === "loadout" && !canViewLoadout
                ? DEFAULT_PROFILE_TAB
                : nextTab,
        );
    }, [canViewLoadout, location.search]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tabParam = params.get("tab");

        if (!tabParam) {
            history.replace({
                pathname: location.pathname,
                search: buildProfileTabSearch(location.search, DEFAULT_PROFILE_TAB),
            });
            return;
        }

        if (!PROFILE_TAB_IDS.includes(tabParam)) {
            history.replace({
                pathname: location.pathname,
                search: buildProfileTabSearch(location.search, getProfileTabFromSearch(location.search)),
            });
            return;
        }

        if (tabParam === "loadout" && !canViewLoadout) {
            history.replace({
                pathname: location.pathname,
                search: buildProfileTabSearch(location.search, DEFAULT_PROFILE_TAB),
            });
        }
    }, [canViewLoadout, history, location.pathname, location.search]);

    const handleTabChange = useCallback((tabId) => {
        const resolvedTab = resolveProfileTab(tabId);
        setActiveTab(resolvedTab);

        const nextSearch = buildProfileTabSearch(location.search, resolvedTab);
        const currentSearch = location.search.startsWith("?")
            ? location.search.slice(1)
            : location.search;

        if (currentSearch !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            });
        }
    }, [history, location.pathname, location.search]);

    const renderActiveTab = () => {
        switch (activeTab) {
            case "matches":
                return (
                    <ProfileMatchHistoryTab
                        last25MatchData={last25MatchData}
                        totalPlayedMatches={matchSummary?.totalPlayedMatches}
                        isLoading={profileDataLoading}
                    />
                );
            case "performance":
                return (
                    <ProfilePerformanceTab
                        last25MatchData={last25MatchData}
                        matchSummary={matchSummary}
                        rating3={rating3}
                        mapsStats={mapsStats}
                        isLoading={profileDataLoading}
                    />
                );
            case "reputation":
                return (
                    <ProfileReputationTab
                        steamId={profileSteamId}
                        isOwnProfile={isOwnProfile}
                    />
                );
            case "friends":
                return (
                    <ProfileFriendsTab
                        friends={isOwnProfile ? viewerFriends : profileFriends}
                        viewerFriends={viewerFriends}
                        isOwnProfile={isOwnProfile}
                        profileNickname={
                            profileData?.nickname || profileData?.name || ""
                        }
                        viewerUser={user}
                        pendingRequests={pendingRequests}
                        onAddFriend={handleSendRequestFriendFromProfilePage}
                        onRemoveFriend={handleUnfriendFromProfilePage}
                    />
                );
            case "loadout":
                return (
                    <ProfileLoadoutTab
                        catalog={catalog}
                        actions={actions}
                        isOwnProfile={isOwnProfile}
                    />
                );
            case "overview":
            default:
                return (
                    <ProfileOverviewTab
                        last25MatchData={last25MatchData}
                        mapsStats={mapsStats}
                        activityMatches={activityMatches}
                        recentStats={recentStats}
                        isLoading={profileDataLoading}
                        onViewAllMatches={() => handleTabChange("matches")}
                        statsBarItems={profileStatsBarItems}
                        statsBarLoading={profileDataLoading}
                    />
                );
        }
    };

    const handleSendRequestFriendFromProfilePage = (steamId) => {
        if (notifyIfCannotSendFriendRequest(user)) {
            return Promise.resolve();
        }

        if (notifyIfTargetCannotReceiveFriendRequest(profileData)) {
            return Promise.resolve();
        }

        if (!steamId) {
            return Promise.resolve();
        }

        if (!viewerSteamId) {
            actions.sendFriendRequestError(
                "Your Steam ID is unavailable. Please try again later.",
            );
            return Promise.resolve();
        }

        setFriendRequestSubmittingSteamId(steamId);
        return actions
            .sendFriendRequest({
                recipientSteamId: steamId,
                userId: user.userId,
                userSteamId: viewerSteamId,
            })
            .finally(() => {
                setFriendRequestSubmittingSteamId((prev) =>
                    prev === steamId ? null : prev,
                );
            });
    };

    const handleCancelFriendRequestFromProfilePage = (steamId) => {
        if (!viewerSteamId) {
            actions.sendFriendRequestError(
                "Your Steam ID is unavailable. Please try again later.",
            );
            return;
        }

        actions.cancelFriendRequest({
            recipientSteamId: steamId,
            userId: user.userId,
            userSteamId: viewerSteamId,
        });
    };

    const handleAcceptFriendRequestFromProfilePage = (steamId) => {
        const targetSteamId = steamId || profileSteamId;
        if (!viewerSteamId || !targetSteamId) {
            actions.sendFriendRequestError(
                "Unable to process request right now. Please try again.",
            );
            return Promise.resolve();
        }

        return actions
            .acceptFriendRequest({
                fromUserSteamId: targetSteamId,
                toUserSteamId: viewerSteamId,
                toUserId: user.userId,
            })
            .then(() =>
                actions.getProfileRelationship({
                    viewerSteamId,
                    targetSteamId,
                }),
            );
    };

    const handleDismissFriendRequestFromProfilePage = (steamId) => {
        const targetSteamId = steamId || profileSteamId;
        if (!viewerSteamId || !targetSteamId) {
            actions.sendFriendRequestError(
                "Unable to process request right now. Please try again.",
            );
            return Promise.resolve();
        }

        return actions
            .declineFriendRequest({
                fromUserSteamId: targetSteamId,
                toUserSteamId: viewerSteamId,
                toUserId: user.userId,
            })
            .then(() =>
                actions.getProfileRelationship({
                    viewerSteamId,
                    targetSteamId,
                }),
            );
    };

    const handleUnfriendFromProfilePage = (steamId) => {
        const targetSteamId = steamId || profileSteamId;
        if (!viewerSteamId || !targetSteamId) {
            actions.sendFriendRequestError(
                "Unable to process request right now. Please try again.",
            );
            return Promise.resolve();
        }

        return actions
            .deleteFriend({
                mySteamId: viewerSteamId,
                friendSteamId: targetSteamId,
                userId: user.userId,
            })
            .then(() =>
                actions.getProfileRelationship({
                    viewerSteamId,
                    targetSteamId,
                }),
            );
    };

    const resolvePlayerStatus = () => {
        if (profileData?.isOnline !== true) return "offline";

        let status = (profileData?.playerStatus || "").trim();
        if (!status || status === "offline") status = "inLobby";

        const viewerLobby = catalog?.currentLobby || null;
        const members = (viewerLobby && viewerLobby.members) || [];
        const isSameLobby = members.some(
            (member) => String(member._id) === String(profileData?._id),
        );

        if (isSameLobby && (status === "inPrivateLobby" || status === "inYourLobby")) {
            return "inYourLobby";
        }

        return status;
    };

    const playerStatus = resolvePlayerStatus();
    // const isFriendRequestSubmitting =
    //     friendRequestSubmittingSteamId === profileSteamId;

    return (
        <Box
            sx={{
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                flex: 1,
                minHeight: MAIN_SECTION_MIN_HEIGHT_SX,
                overflow: "hidden",
            }}
        >
        <MotionBox
            key={id}
            initial={{
                opacity: 0,
                y: 18,
                scale: 0.985,
            }}
            animate={{
                opacity: 1,
                y: 0,
                scale: 1,
            }}
            transition={{
                duration: 0.7,
                ease: [0.22, 1, 0.36, 1],
            }}
            sx={{
                position: "relative",
                minWidth: 0,
                maxWidth: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
                padding: 0,
                overflow: "hidden",
                borderRadius: "10px",
                background: `
                    linear-gradient(
                        180deg,
                        rgba(10,10,12,0.96) 0%,
                        rgba(6,6,8,0.98) 100%
                    )
                `,
                border: "1px solid rgba(255,255,255,0.05)",
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
                boxShadow: `
                    0 20px 40px rgba(0,0,0,0.45),
                    inset 0 1px 0 rgba(255,255,255,0.04)
                `,
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "7rem",
                    background: `
                        radial-gradient(
                            ellipse 70% 100% at top,
                            rgba(232,163,61,0.05),
                            transparent 70%
                        )
                    `,
                    pointerEvents: "none",
                },
                "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "12%",
                    right: "12%",
                    height: "1px",
                    background: `
                        linear-gradient(
                            90deg,
                            transparent,
                            rgba(255,255,255,0.18),
                            transparent
                        )
                    `,
                    pointerEvents: "none",
                },
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0.035,
                    pointerEvents: "none",
                    backgroundImage: `
                        radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                        radial-gradient(circle at 75% 75%, white 1px, transparent 1px)
                    `,
                    backgroundSize: "120px 120px",
                }}
            />
            <MotionBox
                animate={{
                    opacity: [0.08, 0.14, 0.08],
                }}
                transition={{
                    repeat: Infinity,
                    duration: 6,
                    ease: "easeInOut",
                }}
                sx={{
                    position: "absolute",
                    width: "28rem",
                    height: "28rem",
                    borderRadius: "50%",
                    background: `
                        radial-gradient(
                            circle,
                            rgba(232,163,61,0.08),
                            transparent 70%
                        )
                    `,
                    top: "-16rem",
                    right: "-12rem",
                    filter: "blur(60px)",
                    pointerEvents: "none",
                    willChange: "opacity",
                }}
            />

            
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    width: "100%",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <ProfileBanner
                    profileData={profileData}
                    globalRank={globalRank}
                    playerStatus={playerStatus}
                    isOwnProfile={isOwnProfile}
                    isLoading={profileDataLoading}
                />

                <Box
                    sx={{
                        width: "100%",
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                        boxSizing: "border-box",
                        px: { xs: "0.85rem", md: "1.5rem" },
                        pb: { xs: "0.85rem", md: "1.5rem" },
                        [layoutUp]: {
                            display: "grid",
                            gridTemplateColumns: `minmax(0, ${PROFILE_LEFT_RAIL_MAX_PX}px) minmax(0, 1fr)`,
                            alignItems: "start",
                            gap: "1.25rem",
                        },
                    }}
                >
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: "100%",
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.85rem",
                        [layoutUp]: {
                            maxWidth: PROFILE_LEFT_RAIL_MAX_PX,
                        },
                    }}
                >
                    <ProfileHeader
                        profileData={profileData}
                        viewerUser={user}
                        isOwnProfile={isOwnProfile}
                        isLoading={profileDataLoading}
                        friendshipStatus={friendshipStatus}
                        onAddFriend={() => {
                            handleSendRequestFriendFromProfilePage(profileSteamId).then(
                                (data) => {
                                    if (data?.alreadyExists && viewerSteamId) {
                                        actions.getProfileRelationship({
                                            viewerSteamId,
                                            targetSteamId: profileSteamId,
                                        });
                                    }
                                },
                            );
                        }}
                        onAcceptFriend={() => {
                            if (profileSteamId) {
                                handleAcceptFriendRequestFromProfilePage(profileSteamId);
                            }
                        }}
                        onDeclineFriend={() => {
                            if (profileSteamId) {
                                handleDismissFriendRequestFromProfilePage(profileSteamId);
                            }
                        }}
                        onCancelFriendRequest={() => {
                            if (profileSteamId) {
                                handleCancelFriendRequestFromProfilePage(profileSteamId);
                            }
                        }}
                        onReport={() => {}}
                        onEditProfile={() => {
                            history.push("/settings/public-profile");
                        }}
                    />
                    {!profileDataLoading ? <RankCard rankData={rankData} /> : null}
                    {!profileDataLoading ? <ProfileAboutSection profileData={profileData} /> : null}
                </Box>

                <Box
                    sx={{
                        width: "100%",
                        alignSelf: "stretch",
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                    }}
                >
                    <ProfileTabBar
                        value={activeTab}
                        onChange={handleTabChange}
                        tabs={profileTabs}
                    />
                    <MotionBox
                        key={activeTab}
                        initial={{ opacity: 0.68, y: 2 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 0.11,
                            ease: [0.2, 0.8, 0.2, 1],
                        }}
                        sx={{
                            width: "100%",
                            minWidth: 0,
                            mt: "1.75rem",
                            willChange: "opacity, transform",
                        }}
                    >
                        {renderActiveTab()}
                    </MotionBox>
                </Box>
                </Box>
            </Box>
        </MotionBox>
        </Box>
    );
}

ProfilePage.propTypes = {
    actions: PropTypes.object,
    catalog: PropTypes.object.isRequired,
};

export { MAP_NAMES, SORT_GENERAL_VALUES, SORT_MAPS_VALUES };
